import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarDays, Check, Clock3, Repeat2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  daysInMonth,
  formatYmd,
  parseYm,
  type ExpenseCalendarItem,
  type ExpenseCalendarResponse,
  type Ym,
} from '@lira/shared';
import { apiGet } from '@/lib/api';
import { errorMessage } from '@/lib/errorMessage';
import { cn } from '@/lib/cn';
import { CategoryIcon, MoneyText, Skeleton } from '@/components/ui';

const TABS = [
  { id: 'pending', label: 'Pendentes' },
  { id: 'fixed', label: 'Fixas' },
  { id: 'effected', label: 'Realizadas' },
] as const;
type Tab = (typeof TABS)[number]['id'];

function monthBounds(month: Ym): { from: string; to: string } {
  const { year, month: m } = parseYm(month);
  return { from: formatYmd(year, m, 1), to: formatYmd(year, m, daysInMonth(year, m)) };
}

function shortDate(date: string): string {
  const { day, month } = { day: Number(date.slice(8, 10)), month: Number(date.slice(5, 7)) };
  return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}`;
}

function MiniSummary({ label, cents, count, icon: Icon, tone }: { label: string; cents: number; count: number; icon: typeof Clock3; tone: string }) {
  return (
    <div className="min-w-0 rounded-xl bg-canvas px-3 py-3">
      <div className="flex items-center gap-2">
        <span className={cn('grid size-8 shrink-0 place-items-center rounded-full text-white', tone)}><Icon className="size-4" /></span>
        <div className="min-w-0">
          <p className="truncate text-[11px] font-semibold uppercase tracking-wide text-muted">{label}</p>
          <MoneyText cents={cents} bold className="block truncate text-sm" />
        </div>
      </div>
      <p className="mt-1 text-[11px] text-muted">{count} {count === 1 ? 'despesa' : 'despesas'}</p>
    </div>
  );
}

export function ExpenseAgendaCard({ month }: { month: Ym }) {
  const [tab, setTab] = useState<Tab>('pending');
  const { from, to } = monthBounds(month);
  const summaryQuery = useQuery({
    queryKey: ['expense-calendar', from, to, 'all', 'all', 'summary'],
    queryFn: () => apiGet<ExpenseCalendarResponse>(`/expense-calendar?from=${from}&to=${to}&status=all&recurrence=all&view=summary`),
  });
  const tabStatus = tab === 'fixed' ? 'all' : tab;
  const tabRecurrence = tab === 'fixed' ? 'fixed' : 'all';
  const tabQuery = useQuery({
    queryKey: ['expense-calendar', from, to, tabStatus, tabRecurrence, 'agenda', 7],
    queryFn: () => apiGet<ExpenseCalendarResponse>(`/expense-calendar?from=${from}&to=${to}&status=${tabStatus}&recurrence=${tabRecurrence}&limit=7`),
  });
  const items = tabQuery.data?.days.flatMap((day) => day.items) ?? [];
  const tabCount = tab === 'pending'
    ? tabQuery.data?.summary.pendingCount ?? 0
    : tab === 'effected'
      ? tabQuery.data?.summary.effectedCount ?? 0
      : tabQuery.data?.summary.fixedCount ?? 0;

  return (
    <section data-testid="dashboard-expense-agenda" className="rounded-[var(--radius-card)] bg-surface p-4 shadow-card sm:p-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-ink">Agenda de despesas</h2>
          <p className="mt-0.5 text-xs text-muted">Fixas, pendentes e realizadas no mês selecionado.</p>
        </div>
        <Link to={`/calendario?month=${month}&status=all&recurrence=all`} className="inline-flex min-h-10 items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary-text hover:text-primary-text-hover">
          <CalendarDays className="size-4" />Ver calendário
        </Link>
      </div>

      {summaryQuery.isLoading && <div className="mt-4 grid gap-3 sm:grid-cols-3"><Skeleton variant="card" className="h-20" /><Skeleton variant="card" className="h-20" /><Skeleton variant="card" className="h-20" /></div>}
      {summaryQuery.isError && <p className="mt-4 rounded-lg bg-expense-weak px-3 py-4 text-sm text-expense">{errorMessage(summaryQuery.error)}</p>}

      {summaryQuery.data && (
        <>
          <div data-testid="dashboard-expense-agenda-summary" className="mt-4 grid gap-2 sm:grid-cols-3">
            <MiniSummary label="Pendentes" cents={summaryQuery.data.summary.pendingCents} count={summaryQuery.data.summary.pendingCount} icon={Clock3} tone="bg-alert" />
            <MiniSummary label="Realizadas" cents={summaryQuery.data.summary.effectedCents} count={summaryQuery.data.summary.effectedCount} icon={Check} tone="bg-income" />
            <MiniSummary label="Fixas no mês" cents={summaryQuery.data.summary.fixedCents} count={summaryQuery.data.summary.fixedCount} icon={Repeat2} tone="bg-primary" />
          </div>

          <div className="mt-4 flex gap-1 overflow-x-auto border-b border-border" role="tablist" aria-label="Despesas da agenda">
            {TABS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={tab === item.id}
                onClick={() => setTab(item.id)}
                className={cn('min-h-10 shrink-0 border-b-2 px-3 text-xs font-semibold', tab === item.id ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-ink')}
              >
                {item.label}
              </button>
            ))}
          </div>

          {tabQuery.isLoading ? (
            <Skeleton variant="card" className="mt-3 h-32" />
          ) : tabQuery.isError ? (
            <p className="mt-3 rounded-lg bg-expense-weak px-3 py-4 text-sm text-expense">{errorMessage(tabQuery.error)}</p>
          ) : items.length === 0 ? (
            <div className="grid min-h-32 place-items-center text-center">
              <p className="text-sm text-muted">Nenhuma despesa nesta visão.</p>
            </div>
          ) : (
            <ul className="mt-2 divide-y divide-border">
              {items.slice(0, 6).map((item: ExpenseCalendarItem) => (
                <li key={item.id} className="flex min-w-0 items-center gap-3 py-3">
                  <span className="w-11 shrink-0 text-xs font-semibold tabular-nums text-muted">{shortDate(item.date)}</span>
                  <CategoryIcon icon={item.category?.icon} color={item.category?.color ?? '#6515DD'} fallbackText={item.description} size={32} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{item.description || item.category?.name || 'Despesa'}</p>
                    <p className="mt-0.5 truncate text-[11px] text-muted">
                      {item.recurrenceMode === 'fixed' ? 'Fixa · ' : ''}{item.effectuated ? 'Realizada' : 'Pendente'}
                      {item.account?.name || item.card?.name ? ` · ${item.account?.name ?? item.card?.name}` : ''}
                    </p>
                  </div>
                  <MoneyText cents={Math.abs(item.amountCents)} bold className="shrink-0 text-sm" />
                </li>
              ))}
            </ul>
          )}

          {tabCount > 6 && (
            <Link to={`/calendario?month=${month}&status=${tab === 'fixed' ? 'all' : tab}&recurrence=${tab === 'fixed' ? 'fixed' : 'all'}`} className="mt-2 inline-flex min-h-10 items-center text-xs font-semibold uppercase tracking-wide text-primary-text">
              Ver mais {tabCount - 6}
            </Link>
          )}
        </>
      )}
    </section>
  );
}
